import Tippy from '@tippy.js/react';
import classnames from 'classnames';
import moment from 'moment';
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { DayPickerProps } from 'react-day-picker';
import Typography from '~components/typography/typography';
import styles from './day-picker.module.scss';
import ScrollableMonthPicker from './scrollable-month-picker';

const PickerSkeleton: React.FC = () => (
  <div
    style={{
      width: 348,
      background: 'var(--color-white)',
      borderRadius: 14,
      overflow: 'hidden',
      boxShadow: '-2px -2px 8px 0 rgba(0,0,0,0.05), 2px 2px 8px 0 rgba(0,0,0,0.05)',
    }}
  >
    <ContentLoader
      speed={2}
      width={348}
      height={379}
      backgroundColor="#f3f3f3"
      foregroundColor="#ecebeb"
    >
      {/* nav selects */}
      <rect x="100" y="14" rx="4" ry="4" width="60" height="20" />
      <rect x="170" y="14" rx="4" ry="4" width="50" height="20" />
      {/* weekday row */}
      {[0, 1, 2, 3, 4, 5, 6].map(i => (
        <rect key={i} x={12 + i * 47} y="48" rx="4" ry="4" width="30" height="14" />
      ))}
      {/* calendar grid rows */}
      <rect x="24" y="100" rx="8" ry="8" width="300" height="30" />
      <rect x="24" y="150" rx="8" ry="8" width="300" height="30" />
      <rect x="24" y="200" rx="8" ry="8" width="300" height="30" />
      <rect x="24" y="250" rx="8" ry="8" width="300" height="30" />
      <rect x="24" y="300" rx="8" ry="8" width="300" height="30" />
    </ContentLoader>
  </div>
);

interface DayPickerFieldProps {
  value: Date | undefined | null;
  fromMonth: Date;
  toMonth: Date;
  initialMonth?: Date;

  disabledDays?: DayPickerProps['disabledDays'];
  disabled?: boolean;
  loading?: boolean;

  placeholder?: string;
  className?: string;

  onSelectDate?: (value: Date) => void;
  onVisibleChange?: (visible: boolean) => void;
}

export interface DayPickerFieldHandle {
  open: () => void;
  close: () => void;
}

const DayPickerField = React.forwardRef<DayPickerFieldHandle, DayPickerFieldProps>((props, ref) => {
  const {
    className,
    placeholder,
    value,
    fromMonth,
    toMonth,
    initialMonth,
    disabled,
    loading,
    disabledDays,
    onSelectDate,
    onVisibleChange,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [pickerVisible, setPickerVisible] = useState(false);

  const updateVisible = useCallback(
    (v: boolean) => {
      setPickerVisible(v);
      onVisibleChange?.(v);
    },
    [onVisibleChange]
  );

  useImperativeHandle(ref, () => ({
    open: () => updateVisible(true),
    close: () => updateVisible(false),
  }));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        updateVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [updateVisible]);

  return (
    <div className={classnames(styles.root, className)} ref={containerRef}>
      <Tippy
        theme="white"
        appendTo="parent"
        animation="scale"
        interactive={true}
        visible={pickerVisible}
        trigger="manual"
        placement="top-start"
        offset="0,4"
        content={
          loading ? (
            <PickerSkeleton />
          ) : (
            <ScrollableMonthPicker
              fromMonth={fromMonth}
              toMonth={toMonth}
              selectedDate={value || undefined}
              initialMonth={initialMonth}
              disabledDays={disabledDays}
              visible={pickerVisible}
              onSelect={date => {
                if (onSelectDate) onSelectDate(date);
                updateVisible(false);
              }}
            />
          )
        }
      >
        <div
          className={classnames(styles.valueContainer, {
            [styles.valueContainerActive]: pickerVisible || !!value,
            [styles.valueContainerHasValueAndHidden]: !pickerVisible && !!value,
            [styles.valueContainerHasValueAndVisiable]: !!pickerVisible && !!value,
            [styles.valueContainerDisabled]: !!disabled,
          })}
          onClick={() => {
            if (disabled) return;
            updateVisible(!pickerVisible);
          }}
        >
          {!value && placeholder && (
            <Typography
              variant="body3"
              className={classnames(styles.placeholder, {
                [styles.placeholderActive]: pickerVisible,
              })}
            >
              {placeholder}
            </Typography>
          )}
          {!!value && <Typography variant="body3">{moment(value).format('ddd, ll')}</Typography>}
        </div>
      </Tippy>
    </div>
  );
});

export default DayPickerField;
